/**
 * App — root router.
 *
 * URL strategy:
 *   English (default) : /           /search   /scan/:id   /report/:id  ...
 *   Arabic            : /ar         /ar/search /ar/scan/:id ...
 *   Spanish           : /es         /es/search /es/scan/:id ...
 *
 * LanguageWrapper is a layout route that reads the optional :lang param,
 * validates it, applies i18next + document RTL, and renders its <Outlet>.
 * Static segments (/search, /scan/:id …) always beat the dynamic /:lang
 * segment in React Router v7 specificity, so there are no collisions.
 */
import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { LandingPage } from './pages/LandingPage'
import { SearchPage } from './pages/SearchPage'
import { ScanPage } from './pages/ScanPage'
import { ReportPage } from './pages/ReportPage'
import { CustomerReportPage } from './pages/CustomerReportPage'
import { BuilderPage } from './pages/BuilderPage'
import { LivePreviewPage } from './pages/LivePreviewPage'
import { DashboardPage } from './pages/DashboardPage'
import { ProfilePage } from './pages/ProfilePage'
import { LoginPage } from './pages/LoginPage'
import i18n, { SUPPORTED_LANGUAGES, type SupportedLanguage } from './i18n'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min
      retry: 1,
    },
  },
})

// ── Language layout wrapper ───────────────────────────────────────────────────

/**
 * LanguageWrapper reads the :lang URL param (if present), validates it against
 * the supported language list, applies i18next + HTML dir/lang attributes,
 * and renders the child routes via <Outlet>.
 *
 * For the non-prefixed (English) group, `lang` is undefined — English is used.
 * For prefixed groups (/ar/*, /es/*), `lang` is 'ar' or 'es'.
 * Any unrecognised :lang value redirects to /.
 */
function LanguageWrapper() {
  const { lang } = useParams<{ lang?: string }>()

  // Determine whether the /:lang param (if present) is a valid supported language.
  // English uses prefix-free routes so we accept 'en' in SUPPORTED_LANGUAGES only
  // for the non-prefixed group (lang === undefined). Any unrecognised value is invalid.
  const isInvalid = lang != null && !(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)
  const activeLang = (isInvalid ? 'en' : (lang ?? 'en')) as SupportedLanguage

  // Hooks MUST be called unconditionally before any early returns.
  useEffect(() => {
    if (isInvalid) return
    if (i18n.language !== activeLang) {
      i18n.changeLanguage(activeLang)
    }
    document.documentElement.lang = activeLang
    document.documentElement.dir = activeLang === 'ar' ? 'rtl' : 'ltr'
  }, [activeLang, isInvalid])

  if (isInvalid) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* ── Non-prefixed English routes ─────────────────────────────── */}
          <Route element={<LanguageWrapper />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/scan/:scanId" element={<ScanPage />} />
            <Route path="/report/:scanId" element={<ReportPage />} />
            <Route path="/reports/:reportId" element={<CustomerReportPage />} />
            <Route path="/builder/:id" element={<BuilderPage />} />
            <Route path="/builder/:id/live" element={<LivePreviewPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/dashboard/profile" element={<ProfilePage />} />
            <Route path="/login" element={<LoginPage />} />
          </Route>

          {/* ── Language-prefixed routes (/ar/*, /es/*) ──────────────────── */}
          <Route path="/:lang" element={<LanguageWrapper />}>
            <Route index element={<LandingPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="scan/:scanId" element={<ScanPage />} />
            <Route path="report/:scanId" element={<ReportPage />} />
            <Route path="reports/:reportId" element={<CustomerReportPage />} />
            <Route path="builder/:id" element={<BuilderPage />} />
            <Route path="builder/:id/live" element={<LivePreviewPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="dashboard/profile" element={<ProfilePage />} />
            <Route path="login" element={<LoginPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
